import React, { useMemo } from "react";
import { useTheme } from "@material-ui/core/styles";
import { createTheme as createThemeV5, ThemeProvider as ThemeProviderV5 } from "@mui/material/styles";
import { format, parse, isValid } from "date-fns";
import SimpleDatePicker from "../SimpleDatePicker";

const ISO = "yyyy-MM-dd";

const toDate = (s) => {
  if (!s) return null;
  if (s instanceof Date) return isValid(s) ? s : null;
  const d = parse(s, ISO, new Date());
  return isValid(d) ? d : null;
};

const toIso = (d) => (d && isValid(d) ? format(d, ISO) : "");

/**
 * Wrapper sobre o SimpleDatePicker (padrão global) preservando a API antiga
 * (value/onChange como string yyyy-MM-dd). Espelha o tema v4 num ThemeProvider
 * v5 igual ao da Dashboard, pra que os campos herdem cores dark/light corretas.
 */
const DatePickerCustom = ({ value, onChange, label, fullWidth = true, ...rest }) => {
  const themeV4 = useTheme();
  const isDark = themeV4.palette.type === "dark" || themeV4.mode === "dark";
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";

  const v5Theme = useMemo(
    () =>
      createThemeV5({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: { main: PRIMARY_MAIN, dark: PRIMARY_DARK, contrastText: PRIMARY_CONTRAST },
          background: {
            default: isDark ? "#121212" : "#ffffff",
            paper: isDark ? "#0b0d12" : "#ffffff",
          },
          divider: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)",
        },
        components: {
          MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 12 } } },
        },
      }),
    [isDark, PRIMARY_MAIN, PRIMARY_DARK, PRIMARY_CONTRAST]
  );

  return (
    <ThemeProviderV5 theme={v5Theme}>
      <SimpleDatePicker
        value={toDate(value)}
        onChange={(d) => onChange?.(toIso(d))}
        label={label}
        fullWidth={fullWidth}
        {...rest}
      />
    </ThemeProviderV5>
  );
};

export default DatePickerCustom;
